import { PortableText, PortableTextProps } from '@portabletext/react'
import { textBody } from './shared/text-body'
import { cn } from '../../utils/cn'
import { Media, OrMedia, withAspectRatio } from '../or-media'

type OrContentBlockFOrientation = 'normal' | 'reverse'
type TabletPadding = '1rem' | '2rem'

export interface OrContentBlockFProps {
  title: string
  body: PortableTextProps['value']
  mediaOne?: Media | null
  mediaTwo?: Media | null
  orientation?: OrContentBlockFOrientation
  tabletPadding?: TabletPadding
}

export const OrContentBlockF = ({
  title,
  body,
  mediaOne,
  mediaTwo,
  orientation = 'normal',
  tabletPadding = '1rem',
}: OrContentBlockFProps) => {
  return (
    <section
      className={cn(
        'lg:max-w-screen-lg mx-auto px-4 slg:pl-0',
        orientation === 'normal' && 'slg:pr-12 lg:pr-[10.438rem]',
        orientation === 'reverse' && 'lg:pl-[10.438rem]',
        tabletPadding === '2rem' && 'sm:px-8',
        tabletPadding === '1rem' && orientation === 'reverse' && 'slg:pl-4 slg:pr-0',
        tabletPadding === '2rem' && orientation === 'reverse' && 'slg:pl-12 slg:pr-0'
      )}
    >
      <div className={cn('slg:hidden', orientation === 'reverse' && 'flex flex-col-reverse')}>
        <div>
          <div className={cn(orientation === 'normal' ? 'mb-8' : 'mt-8')}>
            <h2 className="text-neutrals-900 font-medium text-24 font-heading mb-4 leading-1.3">{title}</h2>
            {body && <PortableText value={body} components={textBody} />}
          </div>
        </div>
        <div className="relative">
          <div className={cn('flex absolute w-full left-[-1rem]', tabletPadding === '2rem' && 'sm:left-[-2rem]')}>
            <div className="relative z-10 w-[50%] max-w-[18rem] sm:w-[50%] sm:max-w-[30rem]">
              {mediaOne && <OrMedia {...withAspectRatio(mediaOne, '3:4')} className="aspect-[3/4]" />}
            </div>
          </div>
          <div
            className={cn(
              'absolute w-[calc(100%+1rem)] bg-neutrals-100 right-[-1rem] h-[45%] sm:h-[45%]',
              orientation === 'normal' ? 'top-[45%]' : 'bottom-[12%]',
              tabletPadding === '2rem' && 'sm:right-[-2rem] w-[calc(100%+2rem)]'
            )}
          ></div>
          <div className={cn('flex w-full justify-end', orientation === 'normal' ? 'pt-[50%]' : 'mt-[50%]')}>
            <div
              className={cn(
                'relative w-[80%] h-full z-20 max-w-[26rem] sm:w-[80%] sm:max-w-[40rem] sm:right-0 sm:pl-0'
              )}
            >
              {(() => {
                if (!mediaTwo) {
                  return null
                }

                switch (mediaTwo.type) {
                  case 'image':
                    return <OrMedia {...mediaTwo} aspectRatio="16:9" className="slg:max-w-xl" />
                  case 'video':
                    return <OrMedia {...mediaTwo} className="slg:max-w-xl" />
                  case 'model3d':
                    return <OrMedia {...mediaTwo} className="slg:max-w-xl aspect-[16/9]" />
                }
              })()}
            </div>
          </div>
        </div>
      </div>
      <div className="hidden slg:block">
        <div className="relative w-full">
          <div className={cn('lg:gap-20 slg:flex', orientation === 'reverse' && 'slg:flex-row-reverse')}>
            <div
              className={cn('flex slg:w-5/12 lg:w-4/12', orientation === 'normal' ? 'justify-start' : ' justify-end')}
            >
              <div
                className={cn(
                  'relative z-10 left-0 top-[60%] w-[90%] max-w-[24rem] lg:w-[115%] lg:max-w-[30rem]',
                  orientation === 'reverse' && 'lg:right-[15%]'
                )}
              >
                {(() => {
                  if (!mediaOne) {
                    return null
                  }

                  switch (mediaOne.type) {
                    case 'image':
                      return (
                        <OrMedia
                          {...mediaOne}
                          changeDimension
                          aspectRatio="3:4"
                          className="rounded-4 aspect-[3/4] z-10 !h-auto"
                        />
                      )
                    case 'video':
                    case 'model3d':
                      return <OrMedia {...mediaOne} className="rounded-4 aspect-[3/4] z-10 !h-auto" />
                  }
                })()}
              </div>
            </div>
            <div className="slg:w-7/12 lg:w-8/12">
              <div className="slg:text-16 leading-1.4 text-neutrals-700 font-regular mb-6 slg:pl-0">
                <h2 className="text-neutrals-900 font-medium text-32 font-heading mb-4 lg:mb-6 slg:leading-1.2 slg:mb-6">
                  {title}
                </h2>
                {body && <PortableText value={body} components={textBody} />}
              </div>
            </div>
          </div>
        </div>
        <div className={cn('relative slg:flex min-h-80', orientation === 'reverse' && 'slg:flex-row-reverse')}>
          <div
            className={cn(
              'w-9/12 lg:w-11/12 absolute bg-neutrals-100 h-[66%] slg: h-[56%] z-0',
              orientation === 'normal'
                ? 'right-[-3rem] lg:right-[-10.438rem] top-[25%] right-0'
                : 'bottom-[10%] left-0 lg:left-[-10.438rem]',
              tabletPadding === '1rem' && orientation === 'reverse' && 'left-[-1rem]',
              tabletPadding === '2rem' && orientation === 'reverse' && 'left-[-3rem]'
            )}
          ></div>
          <div className="slg:w-7/12 lg:w-8/12 flex justify-end">
            <div
              className={cn(
                'z-20 mt-[26%] w-full max-w-[44rem] lg:w-[75%] lg:max-w-[40rem] lg:mt-[25%]',
                orientation === 'normal' ? 'pl-[20%] lg:pl-0' : 'pr-[20%] lg:pr-[13%]'
              )}
            >
              {(() => {
                if (!mediaTwo) {
                  return null
                }

                switch (mediaTwo.type) {
                  case 'image':
                    return <OrMedia {...mediaTwo} aspectRatio="16:9" className="relative rounded-4 z-20" />
                  case 'video':
                    return <OrMedia {...mediaTwo} className="relative rounded-4 z-20" />
                  case 'model3d':
                    return <OrMedia {...mediaTwo} className="relative rounded-4 z-20 aspect-[16/9]" />
                }
              })()}
            </div>
          </div>
          <div className="slg:w-5/12 lg:w-4/12"></div>
        </div>
      </div>
    </section>
  )
}
